"use client"

import { CircleHelp, Download } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"

type Props = LocaleProps & {
  /** Full QR image URL (either upstream-provided or qrserver fallback). */
  qrSrc: string
  /** Called when the user clicks "Save QR" so callers can fire analytics. */
  onQrSaved?: () => void
}

/**
 * The QR scan card — used inside the Install section when the user has
 * chosen method=qr. Keeps the dot-grid + orange-aura visual treatment from
 * the legacy page so the eSIM "moment" still feels like the centerpiece.
 *
 * Doesn't include the method toggle itself — the parent owns that, so this
 * card stays stateless and easy to swap out for a real qrcode library
 * later (see analytics.ts `install_qr_saved`).
 */
export function QRCard({ lng, qrSrc, onQrSaved }: Props) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-5 shadow-[0_8px_30px_-12px_rgba(28,25,23,0.12)] md:p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgb(244 215 191) 1px, transparent 0)",
          backgroundSize: "22px 22px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />
      <div className="relative grid gap-5 md:grid-cols-[240px_minmax(0,1fr)] md:items-center md:gap-6">
        <div className="mx-auto w-full max-w-[240px]">
          {qrSrc ? (
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-3 -z-10 rounded-[28px] bg-gradient-to-br from-orange-100 via-amber-100 to-orange-200 blur-xl"
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={qrSrc}
                alt={accountText(lng, "install.qrAlt")}
                className="aspect-square w-full rounded-2xl border border-stone-200 bg-white object-contain p-4 shadow-[0_4px_16px_-6px_rgba(28,25,23,0.12)]"
              />
            </div>
          ) : (
            <div className="flex aspect-square w-full items-center justify-center rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-slate-600">
              {accountText(lng, "install.qrUnavailable")}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-[15px] leading-7 text-slate-600">
            {accountText(lng, "install.qrCardBody")}
          </p>
          <div className="flex flex-wrap gap-2">
            {qrSrc ? (
              <a
                href={qrSrc}
                download="yoho-esim-qr.png"
                onClick={onQrSaved}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex h-11 items-center gap-2 rounded-full bg-stone-950 px-5 text-sm font-semibold text-white transition hover:bg-stone-800"
              >
                <Download className="h-4 w-4" aria-hidden />
                {accountText(lng, "install.saveQr")}
              </a>
            ) : null}
          </div>
          <div className="mt-1 flex items-start gap-2 rounded-xl bg-sky-50 p-3 text-xs leading-relaxed text-sky-900">
            <CircleHelp className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{accountText(lng, "install.qrShareWarning")}</span>
          </div>
        </div>
      </div>
    </section>
  )
}
